/**
 * @fileoverview Data Layer (DL) for Adivina Adivinador (Riddle - Tile 10).
 * Handles all HTTP communication with the backend Riddle API endpoints.
 * 
 * @module utilities/DL/Admin/Games/Riddle
 */

/**
 * Base URL helper to guarantee trailing slash compatibility.
 */
const getApiBaseUrl = () => {
    const rawUrl = process.env.REACT_APP_API_URL || '';
    return rawUrl.endsWith('/') ? rawUrl : `${rawUrl}/`;
};

// =============================================================================
// TRIVIA SETS CRUD
// =============================================================================

/**
 * Retrieves all trivia sets for Adivina Adivinador.
 * 
 * @async
 * @function getTriviaSets
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const getTriviaSets = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/trivia-sets`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible obtener los sets de trivias.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener los sets de trivias.'
        };
    }
};

/**
 * Retrieves a single trivia set with full question details.
 * 
 * @async
 * @function getTriviaSetById
 * @param {string} triviaSetId - Unique GUID of the trivia set.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getTriviaSetById = async (triviaSetId) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/trivia-sets/${triviaSetId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible obtener la trivia.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener la trivia.'
        };
    }
};

/**
 * Creates or updates a trivia set.
 * 
 * @async
 * @function saveTriviaSet
 * @param {Object} triviaSetData - Payload containing Title, Description, IsActive, Questions.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const saveTriviaSet = async (triviaSetData) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/trivia-sets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(triviaSetData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible guardar el set de trivia.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al guardar el set de trivia.'
        };
    }
};

/**
 * Deletes a trivia set by ID.
 * 
 * @async
 * @function deleteTriviaSet
 * @param {string} triviaSetId - Unique GUID of the trivia set.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const deleteTriviaSet = async (triviaSetId) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/trivia-sets/${triviaSetId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible eliminar el set de trivia.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Set de trivia eliminado correctamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al eliminar el set de trivia.'
        };
    }
};

// =============================================================================
// QUESTIONS CRUD
// =============================================================================

/**
 * Saves or updates an individual question with its options.
 * 
 * @async
 * @function saveQuestion
 * @param {Object} questionData - Question payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const saveQuestion = async (questionData) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/questions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(questionData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible guardar la pregunta.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al guardar la pregunta.'
        };
    }
};

/**
 * Deletes a question by ID.
 * 
 * @async
 * @function deleteQuestion
 * @param {string} questionId - Unique GUID of the question.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const deleteQuestion = async (questionId) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/questions/${questionId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible eliminar la pregunta.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Pregunta eliminada exitosamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al eliminar la pregunta.'
        };
    }
};

// =============================================================================
// SETTINGS & ACTIVE TRIVIA
// =============================================================================

/**
 * Retrieves general settings and visual customization for Riddle.
 * 
 * @async
 * @function getSettings
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getSettings = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/settings`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible obtener la configuración.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener la configuración.'
        };
    }
};

/**
 * Updates general settings and visual customization for Riddle.
 * 
 * @async
 * @function updateSettings
 * @param {Object} settingsData - Settings payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const updateSettings = async (settingsData) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settingsData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible actualizar la configuración.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al actualizar la configuración.'
        };
    }
};

/**
 * Retrieves the currently active trivia set.
 * 
 * @async
 * @function getActiveTriviaSet
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getActiveTriviaSet = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/active-trivia`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No hay trivia activa configurada.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener la trivia activa.'
        };
    }
};

// =============================================================================
// MEDIA UPLOAD (SKIASHARP PIPELINE)
// =============================================================================

/**
 * Uploads an image file (background, silhouette, response, logo) for SkiaSharp optimization.
 * 
 * @async
 * @function uploadMedia
 * @param {File|Blob} file - Binary image file.
 * @param {string} [assetType='general'] - Type of asset ('silhouette', 'response', 'cover_bg', 'game_bg', 'brand_logo', 'sponsor_logo').
 * @param {string|null} [triviaSetId=null] - Optional associated trivia set GUID.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>}
 */
export const uploadMedia = async (file, assetType = 'general', triviaSetId = null) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (assetType) formData.append('assetType', assetType);
        if (triviaSetId) formData.append('triviaSetId', triviaSetId);

        const response = await fetch(`${getApiBaseUrl()}Riddle/upload-media`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible procesar la imagen.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            url: data.url || data.Url,
            data: data
        };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al subir la imagen.'
        };
    }
};

// =============================================================================
// REAL-TIME TRIGGERS
// =============================================================================

/**
 * Emits command to project cover on /screen.
 * 
 * @async
 * @function showCover
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const showCover = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/show-cover`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible mostrar la portada.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Portada proyectada exitosamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al proyectar la portada.'
        };
    }
};

/**
 * Emits command to project game arena on /screen.
 * 
 * @async
 * @function showGame
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const showGame = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/show-game`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible mostrar el escenario de juego.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Escenario de juego proyectado exitosamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al proyectar el juego.'
        };
    }
};

/**
 * Emits a question to screen and fans with layout and correct answer settings.
 * 
 * @async
 * @function sendQuestion
 * @param {Object} questionPayload - Payload for SendRiddleQuestionRealtimeRequestDto.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const sendQuestion = async (questionPayload) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/send-question`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(questionPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible enviar la adivinanza.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Adivinanza enviada a pantalla y afición.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al enviar la adivinanza.'
        };
    }
};

/**
 * Emits reveal item command to show answer image/text and highlight correct response.
 * 
 * @async
 * @function displayItem
 * @param {Object|null} [questionData=null] - Optional question data.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const displayItem = async (questionData = null) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/display-item`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(questionData || {})
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible revelar el elemento.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Elemento revelado exitosamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al revelar el elemento.'
        };
    }
};

/**
 * Changes active screen layout in real time (1: Left, 2: Right, 3: Only Image, 4: Only Options).
 * 
 * @async
 * @function changeLayout
 * @param {number} screenLayout - Layout mode identifier (1 to 4).
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const changeLayout = async (screenLayout) => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/change-layout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ screenLayout: Number(screenLayout) })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible cambiar el layout.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Layout de pantalla actualizado.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al cambiar el layout.'
        };
    }
};

/**
 * Emits final scores and stadium zone podium to screen.
 * 
 * @async
 * @function sendResults
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const sendResults = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/send-results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible enviar los resultados.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Resultados enviados a pantalla.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al enviar resultados.'
        };
    }
};

/**
 * Resets in-memory results and counters for Riddle.
 * 
 * @async
 * @function resetResults
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const resetResults = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/reset-results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible reiniciar los resultados.`
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data, message: 'Resultados reiniciados correctamente.' };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al reiniciar resultados.'
        };
    }
};

/**
 * Fetches current real-time state snapshot from memory.
 * 
 * @async
 * @function getRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getRealtimeState = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/realtime-state`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible obtener el estado en tiempo real.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener estado en tiempo real.'
        };
    }
};

/**
 * Retrieves accumulated results from backend memory.
 * 
 * @async
 * @function getResults
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const getResults = async () => {
    try {
        const response = await fetch(`${getApiBaseUrl()}Riddle/getResults`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error ${response.status}: No fue posible obtener resultados acumulados.`
            };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener resultados acumulados.'
        };
    }
};

// Aliases for PascalCase naming
export const GetTriviaSets = getTriviaSets;
export const GetTriviaSetById = getTriviaSetById;
export const SaveTriviaSet = saveTriviaSet;
export const DeleteTriviaSet = deleteTriviaSet;
export const SaveQuestion = saveQuestion;
export const DeleteQuestion = deleteQuestion;
export const GetSettings = getSettings;
export const UpdateSettings = updateSettings;
export const GetActiveTriviaSet = getActiveTriviaSet;
export const UploadMedia = uploadMedia;
export const ShowCover = showCover;
export const ShowGame = showGame;
export const SendQuestion = sendQuestion;
export const DisplayItem = displayItem;
export const ChangeLayout = changeLayout;
export const SendResults = sendResults;
export const ResetResults = resetResults;
export const GetRealtimeState = getRealtimeState;
export const GetResults = getResults;
